// DOM Elements

export const MAX_DATA_ENTRIES = 20;

const JOB_ID_REGEX = /^(?:job-[a-f0-9]{8}-[a-f0-9]{8}|[a-f0-9]{16})$/;
const VIEW_AS_STORAGE_KEY = 'ssq_view_as';
const ACTIVE_TEAM_STORAGE_KEY = 'ssq_active_team_id';
const PROJECT_FOCUS_STORAGE_KEY = 'ssq_project_focus_by_scope';

function readSavedTeamID() {
    try {
        const raw = localStorage.getItem(ACTIVE_TEAM_STORAGE_KEY);
        const id = parseInt(raw || '0', 10);
        return Number.isFinite(id) && id > 0 ? id : 0;
    } catch {
        return 0;
    }
}

function readProjectFocusMap() {
    try {
        const raw = localStorage.getItem(PROJECT_FOCUS_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
}

function writeProjectFocusMap(map) {
    try {
        localStorage.setItem(PROJECT_FOCUS_STORAGE_KEY, JSON.stringify(map || {}));
    } catch {}
}

function projectFocusScopeKey(teamID = 0, email = '') {
    const team = Number(teamID || 0);
    if (Number.isFinite(team) && team > 0) return `team:${team}`;
    const cleanEmail = String(email || '').trim().toLowerCase();
    return cleanEmail ? `user:${cleanEmail}` : 'personal';
}

function readSavedProjectFocusForScope() {
    const map = readProjectFocusMap();
    const key = projectFocusScopeKey(state.activeTeamId, state.email);
    const value = String(map[key] || '').trim();
    return value;
}

export function normalizeJobID(value) {
    return String(value || '').trim().toLowerCase();
}

export function isValidJobID(value) {
    if (!value) return false;
    return JOB_ID_REGEX.test(normalizeJobID(value));
}

export function canShowBioAgentsNav() {
    return !!state.isOwner && !state.viewAsEmail;
}

const extSet = (value) => new Set(String(value).trim().split(/\s+/).filter(Boolean));

// Dataset preview configuration for frontend safety guards
export const DATASET_PREVIEW_TEXT_EXTENSIONS = extSet('.txt .index .dbtype .log .json .csv .py .pyw .pl .pm .sh .bash .r .md .fasta .trb .fa .fas .fsa .faa .fna .ffn .frn .mfa .mpfa .afa .a3m .pep .seq .hhr .gb .dna .xml .html .fq .fastq .gbk .embl .gff .gff3 .gtf .bed .bedgraph .wig .vcf .sam .pileup .mpileup .psl .aln .clustal .sto .phy .msf .nwk .tree .tre .nex .nexus .cellml .owl .gmt .obo .mitab .tsv .jsonl .yml .yaml .toml .ini .pkl .mdp .top .itp .ndx');
export const DATASET_PREVIEW_IMAGE_EXTENSIONS = extSet('.png .jpg .jpeg .gif .bmp .svg .webp .tif .tiff .heic .heif');
export const DATASET_PREVIEW_VIDEO_EXTENSIONS = extSet('.mp4 .webm');
export const DATASET_PREVIEW_STRUCTURE_EXTENSIONS = extSet('.pdb .ent .brk .cif .mmcif .mmtf .gro .g96 .sdf .mol .mol2 .pdbqt .xyz .pqr .psf .xtc .trr .dcd');
export const DATASET_PREVIEW_NUMPY_ARCHIVE_EXTENSIONS = extSet('.npz');

export const DATASET_PREVIEW_MAX_BYTES = 2 * 1024 * 1024; // front-end guard to avoid huge previews

function stripCompressionSuffix(p) {
    if (!p) return '';
    return String(p).toLowerCase().replace(/\.(?:gz|gzip|bz2|xz|zip)$/g, '');
}

export function getDatasetPreviewKind(path) {
    if (!path) return null;
    const lower = stripCompressionSuffix(String(path));
    if (!lower) return null;
    for (const ext of DATASET_PREVIEW_VIDEO_EXTENSIONS) {
        if (lower.endsWith(ext)) {
            return 'video';
        }
    }
    for (const ext of DATASET_PREVIEW_IMAGE_EXTENSIONS) {
        if (lower.endsWith(ext)) {
            return 'image';
        }
    }
    for (const ext of DATASET_PREVIEW_STRUCTURE_EXTENSIONS) {
        if (lower.endsWith(ext)) {
            return 'structure';
        }
    }
    for (const ext of DATASET_PREVIEW_NUMPY_ARCHIVE_EXTENSIONS) {
        if (lower.endsWith(ext)) {
            return 'npz';
        }
    }
    for (const ext of DATASET_PREVIEW_TEXT_EXTENSIONS) {
        if (lower.endsWith(ext)) {
            return 'text';
        }
    }
    return null;
}

// State management
export const state = {

    email: '',
    tempEmail: '',

    success: false,
    error: '',
    data: {},

    redUrl: '',

    totalJobs: 0,
    jobFilter: 'all',
    jobProgramFilter: '',
    jobPipelineFilter: '',
    jobs: [],
    job_id: '',
    jobinfo: null,

    jobsLoading: false,
    jobCanceling: false,
    jobInfoLoading: false,

    totalTxs: 0,
    txFilter: 'all',
    txs: [],

    txsLoading: false,

    // Projects
    projects: [],
    activeProjectFocus: '',
    teams: [],
    activeTeamId: readSavedTeamID(),
    activeTeamName: '',

    // Analytics access (special-case owner)
    analyticsAccess: false,

    // Owner-only hidden impersonation
    isOwner: false,
    viewAsEmail: '',

    api_wait_ms: 180,
};

export function setViewAsEmail(email) {
    const clean = String(email || '').trim().toLowerCase();
    state.viewAsEmail = clean;
    try {
        if (state.isOwner && clean) {
            sessionStorage.setItem(VIEW_AS_STORAGE_KEY, clean);
        } else {
            sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
        }
    } catch (_) { /* ignore storage errors */ }
    try { localStorage.removeItem(VIEW_AS_STORAGE_KEY); } catch (_) { /* clear legacy shared value */ }
    try { updateUserDisplay(); } catch {}
}

export function loadSavedViewAs() {
    try {
        const saved = sessionStorage.getItem(VIEW_AS_STORAGE_KEY) || '';
        state.viewAsEmail = state.isOwner ? String(saved || '').trim().toLowerCase() : '';
    } catch (_) {
        state.viewAsEmail = '';
    }
    try { localStorage.removeItem(VIEW_AS_STORAGE_KEY); } catch (_) { /* clear legacy shared value */ }
}

export function impersonationQuery() {
    const params = [];
    if (state.isOwner && state.viewAsEmail) {
        params.push(`user_email=${encodeURIComponent(state.viewAsEmail)}`);
    }
    if (state.activeTeamId) {
        params.push(`team_id=${encodeURIComponent(String(state.activeTeamId))}`);
    }
    return params.join('&');
}

// Page-level cleanup + lightweight polling helpers (cleared on route change)
let currentPageCleanup = null;

export function runPageCleanup() {
    const fn = currentPageCleanup || (typeof window !== 'undefined' ? window.__pageCleanup : null);
    currentPageCleanup = null;
    if (typeof window !== 'undefined') window.__pageCleanup = null;
    if (typeof fn === 'function') {
        try { fn(); } catch (err) { console.warn('page cleanup failed', err); }
    }
}

export function setPageCleanup(fn) {
    currentPageCleanup = typeof fn === 'function' ? fn : null;
    if (typeof window !== 'undefined') window.__pageCleanup = currentPageCleanup;
}

export function startPagePoller(task, intervalMs = 60_000, { runImmediately = false } = {}) {
    if (typeof task !== 'function' || !Number.isFinite(intervalMs) || intervalMs <= 0) return () => {};
    let stopped = false;
    let running = false;
    const tick = async () => {
        if (stopped || running) return;
        if (typeof document !== 'undefined' && document.hidden) return;
        running = true;
        try {
            await task();
        } catch (err) {
            console.error('poll task failed', err);
        } finally {
            running = false;
        }
    };
    if (runImmediately) tick();
    const timer = setInterval(tick, intervalMs);
    return () => {
        stopped = true;
        clearInterval(timer);
    };
}

export function updateUserDisplay() {
    const loggedIn = !!getLoginFlag();
    const safeToggle = (id, hidden) => { const el = getid(id); if (el && el.classList) el.classList.toggle('hidden', hidden); };

    safeToggle('loginButton', loggedIn);
    safeToggle('acct-info', !loggedIn);
    try { document.body.classList.toggle('logged-in', loggedIn); } catch {}

    // Show or hide navigation buttons based on login state
    safeToggle('projects_btn', !loggedIn);
    safeToggle('datasets_btn', !loggedIn);
    safeToggle('new_job_btn', !loggedIn);
    safeToggle('view_jobs_btn', !loggedIn);
    safeToggle('pipelines_btn', !loggedIn);
    safeToggle('templates_btn', !loggedIn);
    safeToggle('bio_agents_btn', !loggedIn || !canShowBioAgentsNav());
    safeToggle('registry_btn', !loggedIn || !canShowBioAgentsNav());
    safeToggle('workshop_btn', !loggedIn || !canShowBioAgentsNav());

    const focusedProject = loggedIn ? String(state.activeProjectFocus || '').trim() : '';
    const projectsBtn = getid('projects_btn');
    if (projectsBtn) {
        const labelEl = projectsBtn.querySelector('.nav-label');
        if (labelEl) labelEl.textContent = 'Projects';
        projectsBtn.setAttribute('aria-label', 'Projects');
        projectsBtn.title = 'Projects';
        projectsBtn.onclick = (event) => {
            if (event && typeof event.preventDefault === 'function') event.preventDefault();
            if (typeof window.navigate === 'function') {
                window.navigate('/projects');
            } else {
                window.location.href = '/projects';
            }
        };
    }
    const focusNav = getid('projectFocusNav');
    const focusName = getid('projectFocusName');
    const focusClear = getid('projectFocusClear');
    if (focusNav) {
        focusNav.classList.toggle('hidden', !focusedProject);
        focusNav.title = focusedProject ? `Focused project: ${focusedProject}` : '';
    }
    if (focusName) {
        focusName.textContent = focusedProject;
        focusName.title = focusedProject;
    }
    if (focusClear) {
        focusClear.title = focusedProject ? `Clear project focus: ${focusedProject}` : 'Clear project focus';
        focusClear.setAttribute('aria-label', focusedProject ? `Clear project focus: ${focusedProject}` : 'Clear project focus');
    }

    if (loggedIn) {
      const emailEl = getid('email-disp');
      const creditsEl = getid('credits-disp');
      const teamEl = getid('activeTeamDisplay');
      const impersonating = state.isOwner && !!state.viewAsEmail;
      if (teamEl) {
        const teamActive = !!state.activeTeamId;
        teamEl.textContent = teamActive ? (state.activeTeamName || `Team ${state.activeTeamId}`) : '';
        teamEl.title = teamActive ? `Active team: ${teamEl.textContent}` : '';
        teamEl.classList.toggle('hidden', !teamActive);
      }
      if (emailEl) {
        emailEl.textContent = state.email || '';
        emailEl.classList.toggle('impersonating', impersonating);
        emailEl.title = state.activeTeamName ? `Team scope: ${state.activeTeamName}` : (impersonating ? 'Impersonating account' : '');
      }
      safeToggle('accountTxsBtn', !!state.activeTeamId);
      safeToggle('accountKeysBtn', !!state.activeTeamId);
      safeToggle('accountTeamsBtn', !!state.activeTeamId);
      if (creditsEl) {
        const teamReadonly = !!state.activeTeamId && !isActiveTeamAdmin();
        creditsEl.textContent = `$${getCredits().toFixed(2)}`;
        creditsEl.classList.toggle('readonly', teamReadonly);
        creditsEl.title = teamReadonly ? 'Only team admins can add team credits.' : '';
        if (teamReadonly) getid('creditsInputContainer')?.classList.add('hidden');
      }
    }

    // Impersonation is indicated on the account button itself.
    const badge = getid('impersonationBadge');
    const badgeText = getid('impersonationText');
    if (badge && badge.classList) badge.classList.add('hidden');
    if (badgeText) {
        badgeText.textContent = 'DEV MODE';
    }

    // Recompute header layout after DOM changes (mobile: one-row vs two-rows)
    try {
        if (typeof window.updateHeaderLayout === 'function') {
            // Defer to next frame so measurements reflect new content
            requestAnimationFrame(() => window.updateHeaderLayout());
        }
    } catch {}
    try {
        document.dispatchEvent(new CustomEvent('ssq-user-display-changed', { detail: { loggedIn } }));
    } catch {}
}

export function setTeams(teams) {
    state.teams = Array.isArray(teams) ? teams : [];
    const active = state.teams.find((t) => Number(t.team_id || 0) === Number(state.activeTeamId || 0));
    if (state.activeTeamId && !active) {
        setActiveTeamId(0);
        return;
    }
    state.activeTeamName = active ? String(active.name || '') : '';
    updateUserDisplay();
}

export function setActiveTeamId(teamID) {
    const id = Number(teamID || 0);
    state.activeTeamId = Number.isFinite(id) && id > 0 ? id : 0;
    const active = state.teams.find((t) => Number(t.team_id || 0) === state.activeTeamId);
    state.activeTeamName = active ? String(active.name || '') : '';
    try {
        if (state.activeTeamId) {
            localStorage.setItem(ACTIVE_TEAM_STORAGE_KEY, String(state.activeTeamId));
        } else {
            localStorage.removeItem(ACTIVE_TEAM_STORAGE_KEY);
        }
    } catch {}
    state.activeProjectFocus = readSavedProjectFocusForScope();
    updateUserDisplay();
}

export function hydrateProjectFocus() {
    state.activeProjectFocus = readSavedProjectFocusForScope();
    updateUserDisplay();
    return state.activeProjectFocus;
}

export function getActiveProjectFocus() {
    return String(state.activeProjectFocus || '').trim();
}

export function setActiveProjectFocus(project) {
    const clean = String(project || '').trim();
    state.activeProjectFocus = clean;
    const key = projectFocusScopeKey(state.activeTeamId, state.email);
    const map = readProjectFocusMap();
    if (clean) {
        map[key] = clean;
    } else {
        delete map[key];
    }
    writeProjectFocusMap(map);
    updateUserDisplay();
    try {
        document.dispatchEvent(new CustomEvent('ssq-project-focus-changed', { detail: { project: clean } }));
    } catch {}
    return clean;
}

export function clearActiveProjectFocus() {
    return setActiveProjectFocus('');
}

export function isActiveTeamAdmin() {
    if (!state.activeTeamId) return true;
    const active = state.teams.find((t) => Number(t.team_id || 0) === Number(state.activeTeamId || 0));
    return !!active?.is_team_admin;
}

export function getid(id) {
    return document.getElementById(id);
}

export function resetPage() {
    const url = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, document.title, url);
    window.location.reload();
}

export function setLoginFlag(isLoggedIn, expiry) {
    if (isLoggedIn) {
        localStorage.setItem('loginFlag', expiry);
    } else {
        localStorage.removeItem('loginFlag');
    }
}

export function getLoginFlag() {
    const expiry = parseInt(localStorage.getItem('loginFlag'), 10);
    if (!expiry || Math.floor(Date.now() / 1000) > expiry) {
        localStorage.removeItem('loginFlag');
        return false;
    }
    return expiry;
}

export function setCredits(credits) {
    localStorage.setItem('credits', credits.toFixed(2));
}

export function getCredits() {
    return parseFloat(localStorage.getItem('credits')) || 0;
}

export function clearClientAuthState() {
    state.email = '';
    state.tempEmail = '';
    state.analyticsAccess = false;
    state.isOwner = false;
    state.viewAsEmail = '';
    state.teams = [];
    state.activeTeamId = 0;
    state.activeTeamName = '';
    state.activeProjectFocus = '';
    try { localStorage.removeItem('loginFlag'); } catch {}
    try { localStorage.removeItem('credits'); } catch {}
    try { localStorage.removeItem(ACTIVE_TEAM_STORAGE_KEY); } catch {}
    try { sessionStorage.removeItem(VIEW_AS_STORAGE_KEY); } catch {}
    try { localStorage.removeItem(VIEW_AS_STORAGE_KEY); } catch {}
    try { delete window.subseqSetViewAs; } catch {}
    updateUserDisplay();
}

// Format a timestamp to 'YYYY-MM-DD HH:MM' (24h)
// Accepts epoch seconds, epoch milliseconds, or parseable date strings
export function formatDateTime(ts) {
    if (ts === null || ts === undefined || ts === '') return '';
    try {
        let d;
        if (typeof ts === 'number') {
            // Heuristic: treat < 1e11 as seconds, otherwise ms
            d = new Date(ts < 1e11 ? ts * 1000 : ts);
        } else if (typeof ts === 'string') {
            // Numeric strings → parse similarly to numbers
            const num = Number(ts);
            if (!Number.isNaN(num) && ts.trim() !== '') {
                d = new Date(num < 1e11 ? num * 1000 : num);
            } else {
                d = new Date(ts);
            }
        } else if (ts instanceof Date) {
            d = ts;
        } else {
            return '';
        }
        if (Number.isNaN(d.getTime())) return '';
        const pad = (n) => String(n).padStart(2, '0');
        const yyyy = d.getFullYear();
        const mm = pad(d.getMonth() + 1);
        const dd = pad(d.getDate());
        const HH = pad(d.getHours());
        const MM = pad(d.getMinutes());
        return `${yyyy}-${mm}-${dd} ${HH}:${MM}`;
    } catch {
        return '';
    }
}
